/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IconButton } from '@fluentui/react/lib/Button';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { Stack } from '@fluentui/react/lib/Stack';
import { getId, IsFocusVisibleClassName } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { DirectoriesSettings } from 'src/appShell/globalHeader/globalHeaderBlades/settingsBlade/DirectoriesSettings';
import { GeneralSettings } from 'src/appShell/globalHeader/globalHeaderBlades/settingsBlade/GeneralSettings';
import { ResourcesSettings } from 'src/appShell/globalHeader/globalHeaderBlades/settingsBlade/ResourcesSettings';
import { GlobalSettingsBladeTabsType } from 'src/appShell/globalHeader/types';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { LanguageStudioSubHeader } from 'src/languageStudio/appShell/landingPage/components/LanguageStudioSubHeader';

const messages = defineMessages({
    settingsTitle: { id: 'languageStudio.globalSettingsBlade.settingsTitle', defaultMessage: 'Settings' },
    generalTabTitle: { id: 'languageStudio.globalSettingsBlade.generalTabTitle', defaultMessage: 'General' },
    closeButtonTitle: { id: 'languageStudio.globalSettingsBlade.closeButtonTitle', defaultMessage: 'Close blade' },
    directoryTabTitle: { id: 'languageStudio.globalSettingsBlade.directoryTabTitle', defaultMessage: 'Directory' },
    resourcesTabTitle: { id: 'languageStudio.globalSettingsBlade.resourcesTabTitle', defaultMessage: 'Resources' }
});

export const GlobalSettingsBlade = observer(() => {
    const { localize } = useLocalization();
    const { dispatcher, uiStore } = useGlobalState();

    const activeTab = uiStore.currentActiveSettingTab;
    const isLanguageStudio = uiStore.portal.kind === 'languageStudio';

    const changeTab = (settingsTab: GlobalSettingsBladeTabsType) =>
        dispatcher.dispatch('updateActiveGlobalBlade', { settingsTab, newBlade: 'settings' });

    return (
        <Stack verticalFill>
            {isLanguageStudio && (
                <LanguageStudioSubHeader onRootClick={() => dispatcher.dispatch('updateActiveGlobalBlade', { newBlade: null })} />
            )}

            <Stack styles={{ root: { flex: 1, minHeight: 0, marginLeft: '0.5%' } }} tokens={{ childrenGap: 22 }}>
                <Stack horizontal horizontalAlign="space-between">
                    <LuisText as="h1" variant="xLargePlus" fontWeight={600}>
                        {localize(messages.settingsTitle)}
                    </LuisText>

                    <LuisTooltip
                        hidden={document.body.className.indexOf(IsFocusVisibleClassName) === -1}
                        content={localize(messages.closeButtonTitle)}>
                        <IconButton
                            id={getId('iconButton')}
                            iconProps={{ iconName: 'ChromeClose' }}
                            title={localize(messages.closeButtonTitle)}
                            ariaLabel={localize(messages.closeButtonTitle)}
                            data-automation-id="CloseLanguageStudioSettingsBladeButton"
                            onClick={() => dispatcher.dispatch('updateActiveGlobalBlade', { newBlade: null })}
                        />
                    </LuisTooltip>
                </Stack>

                <Pivot selectedKey={activeTab} onLinkClick={item => changeTab(item.props.itemKey as GlobalSettingsBladeTabsType)}>
                    <PivotItem itemKey="directories" headerText={localize(messages.directoryTabTitle)} />
                    <PivotItem itemKey="resources" headerText={localize(messages.resourcesTabTitle)} />
                    <PivotItem itemKey="general" headerText={localize(messages.generalTabTitle)} />
                </Pivot>

                {activeTab === 'general' && <GeneralSettings />}
                {activeTab === 'resources' && <ResourcesSettings />}
                {activeTab === 'directories' && <DirectoriesSettings />}
            </Stack>
        </Stack>
    );
});
